import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarIcon, CheckCircle2, ArrowRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Hero background image - HD 4K modern apartment building */
const HERO_BG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/xkrltnjr_b250a631-99c5-4266-9cca-f4088a991a50.png";

const CONTACT_INFO = [
  { icon: MapPin, label: "Address", value: "Melbourne, VIC", detail: "Servicing all Melbourne metro areas" },
  { icon: Phone, label: "Phone", value: "(03) 9114 9576", detail: "Mon - Fri, 7am - 6pm AEST" },
  { icon: Mail, label: "Email", value: "admin@pkcpropertyservices.com.au", detail: "We respond within 24 hours" },
  { icon: Clock, label: "Hours", value: "24/7 Available", detail: "Emergency cleaning available" },
];

export default function BookConsultPage() {
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("https://formspree.io/f/meewjkrv", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service_type: formData.service_type,
          preferred_date: date ? format(date, "yyyy-MM-dd") : "Not specified",
          message: formData.message,
        }),
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success("Your message has been sent successfully!");
      } else {
        throw new Error("Form submission failed");
      }
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="consult-success" className="min-h-screen flex items-center justify-center bg-white pt-20">
        <div className="text-center max-w-md px-6">
          <CheckCircle2 className="w-16 h-16 text-[#B8D86B] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-neutral-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Thank You
          </h2>
          <p className="text-neutral-600 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Your message has been received. Our team will be in touch within 24 hours.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-8 py-3.5 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Back to Home
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-consult-page" className="bg-white">
      {/* Hero - Job site photo with overlay */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-20 relative overflow-hidden min-h-[60vh] md:min-h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="" 
            className="w-full h-full object-cover object-[center_40%]"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contact Us
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get in Touch<span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Have a question or ready to get started? Fill out the form below and our team will get back to you within 24 hours.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 border-b border-neutral-200 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONTACT_INFO.map((info, idx) => (
              <ScrollReveal key={info.label} delay={idx * 0.1}>
                <div className="bg-white p-6 md:p-8 h-full hover:shadow-md transition-shadow duration-300 border border-neutral-100" data-testid={`contact-info-${idx}`}>
                  <info.icon className="w-6 h-6 text-[#B8D86B] mb-4" strokeWidth={1.5} />
                  <p className="text-xs font-semibold tracking-[0.15em] uppercase text-neutral-400 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{info.label}</p>
                  <p className="text-neutral-900 text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{info.value}</p>
                  <p className="text-neutral-500 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{info.detail}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <ScrollReveal>
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="consult-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Full Name <span className="text-[#B8D86B]">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="consult-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="rounded-none border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-[#B8D86B] focus:ring-[#B8D86B] h-12"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Email <span className="text-[#B8D86B]">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="consult-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="rounded-none border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-[#B8D86B] focus:ring-[#B8D86B] h-12"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="consult-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+61 XXX XXX XXX"
                        className="rounded-none border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-[#B8D86B] focus:ring-[#B8D86B] h-12"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="consult-company-input"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Company name"
                        className="rounded-none border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-[#B8D86B] focus:ring-[#B8D86B] h-12"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>Service Type</Label>
                      <Select
                        value={formData.service_type}
                        onValueChange={(val) => setFormData((prev) => ({ ...prev, service_type: val }))}
                      >
                        <SelectTrigger
                          data-testid="consult-service-select"
                          className="rounded-none border-neutral-300 bg-white text-neutral-900 h-12"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          <SelectValue placeholder="Select a service (optional)" className="text-neutral-400" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-neutral-200">
                          <SelectItem value="general-inquiry" className="text-neutral-900">General Inquiry</SelectItem>
                          <SelectItem value="builders-cleaning" className="text-neutral-900">Builder's Cleaning</SelectItem>
                          <SelectItem value="industrial-cleaning" className="text-neutral-900">Industrial Cleaning</SelectItem>
                          <SelectItem value="pressure-washing" className="text-neutral-900">Pressure Washing</SelectItem>
                          <SelectItem value="facilities-management" className="text-neutral-900">Facilities Management</SelectItem>
                          <SelectItem value="office-cleaning" className="text-neutral-900">Office Cleaning</SelectItem>
                          <SelectItem value="window-abseiling" className="text-neutral-900">Window & Abseiling</SelectItem>
                          <SelectItem value="deep-cleaning" className="text-neutral-900">Deep Cleaning</SelectItem>
                          <SelectItem value="other" className="text-neutral-900">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="consult-date-picker"
                            className={`w-full rounded-none border-neutral-300 bg-white text-neutral-900 h-12 justify-start text-left font-normal hover:bg-neutral-50 ${
                              !date && "text-neutral-400"
                            }`}
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : "Pick a date (optional)"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-neutral-200" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(d) => d < new Date()}
                            initialFocus
                            className="bg-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-neutral-700 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Message <span className="text-[#B8D86B]">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="consult-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or inquiry..."
                      className="rounded-none border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-[#B8D86B] focus:ring-[#B8D86B] min-h-[140px]"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="consult-submit-btn"
                    disabled={submitting}
                    className="bg-[#B8D86B] text-neutral-900 hover:bg-[#A3C65A] rounded-none px-10 py-6 text-sm font-semibold w-full md:w-auto"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {submitting ? "Sending..." : "Send Message"}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </form>
              </ScrollReveal>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <ScrollReveal delay={0.2}>
                <div className="bg-neutral-50 p-8 md:p-10 border border-neutral-200">
                  <h3 className="text-lg font-semibold text-neutral-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>What to Expect</h3>
                  <Separator className="w-8 bg-[#B8D86B] h-0.5 mb-6" />
                  <div className="space-y-6">
                    {[
                      { step: "01", title: "Quick Response", desc: "Our team will reach out within 24 hours to discuss your requirements." },
                      { step: "02", title: "Site Assessment", desc: "We'll arrange a site visit to understand the scope and provide an accurate quote." },
                      { step: "03", title: "Tailored Proposal", desc: "You'll receive a detailed proposal with timeline, pricing and methodology." },
                      { step: "04", title: "Project Delivery", desc: "Our dedicated project manager ensures seamless execution and handover." },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4">
                        <span className="text-[#B8D86B] text-sm font-bold shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.step}</span>
                        <div>
                          <p className="text-neutral-900 text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</p>
                          <p className="text-neutral-500 text-xs leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
